import React, { useState, useEffect } from "react";
import "../Styles/Login.scss";
import { useMsal } from "@azure/msal-react";
import { loginRequest, b2cPolicies } from "../authConfig";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  User,
  Building,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import PortalPoliciesModal from "./PortalPolicies";

const Login = ({ showPoliciesOnLoad = false }) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPolicyModal, setShowPolicyModal] = useState(showPoliciesOnLoad);
  const [isSignUp, setIsSignUp] = useState(location.pathname === "/register");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  // Progressive Blur-to-Sharp Background Preloader
  useEffect(() => {
    const torusImg = new Image();
    torusImg.src = require("../images/banners/login-banner-3d-torus3.png");
    if (torusImg.complete) {
      setBgLoaded(true);
    } else {
      torusImg.onload = () => setBgLoaded(true);
      torusImg.onerror = () => setBgLoaded(true);
    }
  }, []);

  useEffect(() => {
    setIsSignUp(location.pathname === "/register");
  }, [location.pathname]);

  // Catch Microsoft Azure AD returned account dynamically for ANY user
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const activeAccount = accounts[0];
      const claims = activeAccount.idTokenClaims || {};
      console.log("🔍 [Auth Debug] MSAL Active Account:", activeAccount);
      console.log("🔍 [Auth Debug] MSAL ID Token Claims:", claims);

      const realEmail =
        (Array.isArray(claims.emails) && claims.emails[0]) ||
        claims.email ||
        claims.preferred_username ||
        claims.upn ||
        claims.unique_name ||
        (Array.isArray(claims.otherMails) && claims.otherMails[0]) ||
        activeAccount.username ||
        sessionStorage.getItem("user_email") ||
        localStorage.getItem("user_email") ||
        "";

      const realName =
        claims.name ||
        claims.given_name ||
        (claims.given_name && claims.family_name ? `${claims.given_name} ${claims.family_name}` : null) ||
        activeAccount.name ||
        (realEmail && realEmail.includes("@")
          ? realEmail.split("@")[0].replace(/[._+-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
          : "Partner User");

      if (realEmail) {
        sessionStorage.setItem("user_email", realEmail);
        sessionStorage.setItem("user_name", realName);
        localStorage.setItem("user_email", realEmail);
        localStorage.setItem("user_name", realName);
      }

      navigate("/");
    }
  }, [accounts, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }
    setLoading(true);
    const loginEmail = email.trim();
    const derivedName = fullName.trim() || loginEmail.split("@")[0].replace(/[._+-]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    
    sessionStorage.setItem("user_email", loginEmail);
    sessionStorage.setItem("user_name", derivedName);
    localStorage.setItem("user_email", loginEmail);
    localStorage.setItem("user_name", derivedName);
    if (companyName) {
      sessionStorage.setItem("company_name", companyName);
      localStorage.setItem("company_name", companyName);
    }

    setTimeout(() => {
      setLoading(false);
      navigate("/");
    }, 600);
  };

  const handleSSOLogin = () => {
    setLoading(true);

    if (!instance) {
      setTimeout(() => {
        setLoading(false);
        navigate("/");
      }, 500);
      return;
    }

    try {
      instance.loginRedirect(loginRequest).catch((e) => {
        console.error("SSO Login error:", e);
        if (e && (e.errorMessage?.includes("AADB2C90118") || e.message?.includes("AADB2C90118"))) {
          instance.loginRedirect(b2cPolicies.authorities.forgotPassword);
          return;
        }
        setLoading(false);
      });
    } catch (err) {
      console.error("SSO redirect exception:", err);
      setLoading(false);
    }
  };

  return (
    <div className={`partner-login-page ${bgLoaded ? "is-bg-loaded" : ""}`}>
      {/* Progressive Ambient Studio Blur-Up Layer */}
      <div className="partner-bg-layer" />

      {/* Centered Max-Width Container Wrapper */}
      <div className="partner-login-container">
        {/* Left Hero Content Section */}
        <div className="partner-login-left">
          {/* Top Logo Header with Subtitle */}
          <div className="partner-logo-container">
            <img
              src="/images/brand/inextlabs-logo.png"
              alt="iNextLabs Logo"
              className="partner-logo-img"
            />
            <span className="partner-logo-divider">|</span>
            <span className="partner-logo-subtext">Partner Portal</span>
          </div>

          {/* Middle Hero Content */}
          <div className="partner-left-middle-block">
            <h1 className="partner-hero-headline">Stronger partnerships.<br />Limitless <span className="partner-orange-text">impact.</span></h1>


            <p className="partner-hero-subtext">
              Access the iNextLabs Partner Portal to manage opportunities,
              enable success, and grow together.
            </p>
          </div>

          {/* Left Footer */}
          <div className="partner-left-footer">
            <div className="partner-footer-copyright">
              © {new Date().getFullYear()} iNextLabs. All rights reserved.
            </div>
            <div className="partner-footer-links">
              <button
                type="button"
                className="partner-footer-link-btn"
                onClick={() => setShowPolicyModal(true)}
              >
                Privacy Policy
              </button>
              <span className="partner-footer-separator">|</span>
              <button
                type="button"
                className="partner-footer-link-btn"
                onClick={() => setShowPolicyModal(true)}
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>

        {/* Right Form Card Section matching Mockup 1:1 */}
        <div className="partner-login-right-wrapper">
          <div className="partner-login-card">
            {/* Top Tag & Title */}
            <div className="partner-card-tag">PARTNER Portal</div>
            <h2 className="partner-card-title">
              {isSignUp ? "Create an account" : "Welcome back"}
            </h2>
            <p className="partner-card-subtext">
              {isSignUp
                ? "Sign up to access the iNextLabs Partner Portal."
                : "Sign in to access the iNextLabs Partner Portal."}
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="partner-login-form">
              {/* Full Name Field (Sign Up mode) */}
              {isSignUp && (
                <div className="partner-form-group">
                  <label htmlFor="fullName">Full name</label>
                  <div className="partner-input-wrapper">
                    <User className="partner-input-icon" size={18} />
                    <input
                      id="fullName"
                      type="text"
                      required
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Company Name Field (Sign Up mode) */}
              {isSignUp && (
                <div className="partner-form-group">
                  <label htmlFor="companyName">Company name</label>
                  <div className="partner-input-wrapper">
                    <Building className="partner-input-icon" size={18} />
                    <input
                      id="companyName"
                      type="text"
                      required
                      placeholder="Acme Corporation"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div className="partner-form-group">
                <label htmlFor="email">
                  {isSignUp ? "Work email" : "Sign in with your email address"}
                </label>
                <div className="partner-input-wrapper">
                  <Mail className="partner-input-icon" size={18} />
                  <input
                    id="email"
                    type="email"
                    required
                    placeholder="marketingteam@inextlabs.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="partner-form-group">
                <div className="partner-label-row">
                  <label htmlFor="password">Password</label>
                  {!isSignUp && (
                    <Link to="/forgot-password" className="partner-forgot-link">
                      Forgot your password?
                    </Link>
                  )}
                </div>
                <div className="partner-input-wrapper">
                  <Lock className="partner-input-icon" size={18} />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="partner-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Vibrant Coral Primary CTA Button */}
              <button
                type="submit"
                disabled={loading}
                className="partner-submit-btn"
              >
                <span>
                  {loading
                    ? isSignUp
                      ? "Creating account..."
                      : "Signing in..."
                    : isSignUp
                    ? "Create account"
                    : "Sign in"}
                </span>
                <ArrowRight size={18} className="btn-arrow" />
              </button>
            </form>

            {/* Social Account / Team SSO Divider */}
            <div className="partner-divider">
              <span>Sign in with your social account</span>
            </div>

            {/* SSO / iNextLabs Team Button */}
            <button
              type="button"
              disabled={loading}
              className="partner-sso-btn"
              onClick={handleSSOLogin}
            >
              <ShieldCheck size={18} className="partner-sso-icon" />
              <span>{loading ? "Connecting..." : "iNextLabs Team"}</span>
            </button>

            {/* Toggle Mode Footer */}
            <div className="partner-signup-row">
              {isSignUp ? (
                <>
                  <span>Already a partner? </span>
                  <button
                    type="button"
                    className="partner-signup-link-btn"
                    onClick={() => setIsSignUp(false)}
                  >
                    <span>Sign in</span>
                    <ArrowRight size={14} />
                  </button>
                </>
              ) : (
                <>
                  <span>Don't have an account? </span>
                  <button
                    type="button"
                    className="partner-signup-link-btn"
                    onClick={() => setIsSignUp(true)}
                  >
                    <span>Not a Partner yet? Sign up now</span>
                    <ArrowRight size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Portal Usage Policies & Terms Modal */}
      <PortalPoliciesModal
        showModal={showPolicyModal}
        handleAgree={() => setShowPolicyModal(false)}
      />
    </div>
  );
};

export default Login;
